import { Link, useNavigate } from "@tanstack/react-router";

import type { CheckoutPreview } from "@zx/contracts";

import { Img, Price } from "../ui.tsx";
import { readPurchaseAs, readViewAs, writeIdentity } from "../../lib/identity.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  顶栏购物车的**悬停预览**
 * ═══════════════════════════════════════════════════════════════
 *
 * 老板指定，参考 twshangcheng 的 `CartPreview`（那边是隐形眼镜的配送单）。
 * 滑过顶栏那个购物车图标 → 掀开一张卡：车里有什么、合计多少、两个出口。
 *
 * ⚠️ **这是个捷径，不是购物车页**：只列前几件、**不给改数量**。
 *    改数量要重算价格链、要处理库存不足和调价（`PRICE_CHANGED`），
 *    那些提示在一张小卡里放不下 —— 做半套比不做更糟。
 *
 * 🔴 **店铺预览态（我在看自己的店）不给结算。** 那个价是客户看到的价，
 *    按它下单会生成一张"自己卖给自己"的单，而且跟服务端按真实身份算的价
 *    对不上、会被 `PRICE_CHANGED` 拦下。这条规则跟购物车页是同一份判断，
 *    两边写岔的话，这里放行、点进去被拦，用户只觉得系统坏了。
 */

/** 最多列几件。再多只说"还有 N 件" —— 这张卡不该长到盖住半个屏幕。 */
const PREVIEW_MAX = 4;

export function CartPreview({ cart }: { cart: CheckoutPreview | undefined }) {
  const navigate = useNavigate();

  /** 🔴 跟 `routes/desktop/cart.tsx` 同一份判断，见文件头。 */
  const previewing = readPurchaseAs() !== null && readViewAs() === "shop";

  /**
   * ⚠️ 购物车按**供应商分组**（下单时按供应商拆单），但预览里不分组：
   *    一张 320px 的卡上再套一层分组标题，四件商品能占掉一半高度，
   *    而"这几件分几单发"是结算页要交代的事，不是这里。
   */
  const lines = (cart?.groups ?? []).flatMap((g) => g.lines);
  const shown = lines.slice(0, PREVIEW_MAX);
  const rest = lines.length - shown.length;

  return (
    <div className="w-[336px] overflow-hidden rounded-xl border border-border bg-card shadow-lg">
      {lines.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <p className="text-[13px] text-muted-foreground">购物车还是空的</p>
          {/* ⚠️ 空态要给出路 —— 只说一句"空的"等于让人自己想下一步去哪。 */}
          <Link to="/products" className="mt-2 inline-block text-[13px] text-brand-600">
            去挑点建材 →
          </Link>
        </div>
      ) : (
        <>
          <ul className="max-h-[19rem] divide-y divide-border overflow-y-auto">
            {shown.map((l) => (
              <li key={l.skuId} className="flex gap-3 px-4 py-3">
                <Img
                  src={l.imageUrl}
                  alt={l.title}
                  className="size-12 shrink-0 rounded-lg border border-border object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px]">{l.title}</p>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">
                    <Price cents={l.unitPrice} className="text-[12px]" /> × {l.quantity}
                  </p>
                </div>
                <Price cents={l.subtotal} className="shrink-0 text-[13px]" />
              </li>
            ))}
          </ul>

          {rest > 0 && (
            <p className="border-t border-border px-4 py-2 text-center text-[12px] text-muted-foreground">
              还有 {rest} 件
            </p>
          )}

          <div className="border-t border-border px-4 py-3">
            <div className="flex items-baseline justify-between">
              <span className="text-[13px] text-muted-foreground">合计</span>
              <Price cents={cart?.totalPayable ?? 0} className="text-[17px] text-brand-600" />
            </div>

            {/*
              ⚠️ 车里有下架 / 缺货的商品，**在这儿就说**，别等他点进结算才被挡。
                 只列第一条：小卡放不下全部，详细的在购物车页。
            */}
            {cart?.issues?.[0] && (
              <p className="mt-2 text-[12px] leading-4 text-amber-700">
                {cart.issues[0].title ? `「${cart.issues[0].title}」` : "有商品"}
                {cart.issues[0].reason}
              </p>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2">
              <Link
                to="/cart"
                className="flex h-9 items-center justify-center rounded-lg border border-border text-[13px] transition-colors hover:bg-secondary"
              >
                查看购物车
              </Link>
              {/*
                🔴 预览客户价时按钮不是"禁用"而是**换成一条出路**。
                   只把它灰掉的话用户就卡在那儿了 —— 他不知道为什么不能买、
                   也不知道该怎么办。点一下切回「我自己」接着买。
                   （跟商品详情页那颗「按我的进货价买」同一个处理。）
              */}
              <button
                type="button"
                onClick={
                  previewing ? () => writeIdentity("") : () => void navigate({ to: "/checkout" })
                }
                className="flex h-9 items-center justify-center rounded-lg bg-brand-600 px-2 text-[13px] text-white transition-colors hover:bg-brand-700"
              >
                {previewing ? "按我的进货价结算" : "去结算"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
